import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  formatMoney,
  Timestamp,
  type Trip,
  type TripBudget,
  type TripMemberEntry,
} from "@travelix/core";
import { Link } from "react-router-dom";
import {
  Camera,
  ChevronRight,
  Users,
  Calendar,
  Bell,
  DollarSign,
  LogOut,
  Trash2,
  Check,
  Sparkles,
  Link2,
} from "lucide-react";

import { isLikelyBrokenCoverUrl } from "@/lib/cover-url";
import { parseYmdLocal } from "@/lib/trip-date-bounds";
import { InviteLinksSheet } from "@/components/invite-links-sheet";

import { useTrip } from "@/hooks/use-trip";
import { useAuth } from "@/lib/auth-provider";
import { NavHeader } from "@/components/nav-header";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarGroup } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/ui/field";
import { Dialog, DialogContent, DialogHeader, DialogFooter, ConfirmDialog } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetBody, SheetFooter } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import {
  deleteTrip,
  inviteUserToTrip,
  leaveTrip,
  removeMemberFromTrip,
  updateTripBudget,
  updateTripCurrency,
  updateTripName,
  updateTripSummaryFields,
  unwrap,
  queryKeys,
} from "@/lib/api";
import { useTripMutation } from "@/hooks/use-trip-mutation";

const SUPPORTED_CURRENCIES = ["EUR", "USD", "GBP", "JPY", "CHF", "SEK", "MXN", "BRL", "AUD", "CAD"] as const;

export default function TripSettingsRoute() {
  const { id } = useParams<{ id: string }>();
  const { data: trip, isLoading } = useTrip(id);
  const { user } = useAuth();

  const [renameOpen, setRenameOpen] = useState(false);
  const [datesOpen, setDatesOpen] = useState(false);
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [inviteLinksOpen, setInviteLinksOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Owner check is UX-only — Firestore rules gate via memberIds. We resolve
  // "am I the owner?" by matching on UID first (the canonical shape) and
  // falling back to email match, since some v1 trips have members{} keyed
  // by email rather than UID. Either shape is acceptable at the data layer.
  const currentEmail = user?.email?.toLowerCase() ?? null;
  const isOwner =
    !!trip &&
    !!user?.uid &&
    (trip.members[user.uid]?.role === "owner" ||
      Object.values(trip.members).some(
        (m) => m.role === "owner" && m.email?.toLowerCase() === currentEmail,
      ));

  return (
    <main className="min-h-full bg-bg pb-12">
      <NavHeader title="Trip settings" showBack />

      {isLoading || !trip ? (
        <div className="mx-auto max-w-2xl px-5 pt-6 flex flex-col gap-4" aria-busy>
          <Skeleton className="h-32 w-full rounded-xl" />
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-24 w-full rounded-xl" />
        </div>
      ) : (
        <div className="mx-auto max-w-2xl px-5 pt-4 flex flex-col gap-6">
          <CoverPhotoRow trip={trip} isOwner={isOwner} />

          <button
            type="button"
            onClick={() => isOwner && setRenameOpen(true)}
            disabled={!isOwner}
            className="mx-auto block max-w-full rounded-md px-2 py-1 text-center hover:bg-bg-sunken/60 disabled:cursor-default disabled:hover:bg-transparent"
          >
            <h2
              className="font-display leading-tight tracking-tight text-ink m-0 truncate"
              style={{ fontVariationSettings: `"opsz" 60, "wght" 500`, fontSize: 32 }}
            >
              {trip.name}
            </h2>
            {isOwner ? (
              <p className="mt-1 text-[12px] text-ink-subtle">Tap to rename</p>
            ) : null}
          </button>

          <Section title="Group">
            <SettingRow
              label="Members"
              onPress={() => setParticipantsOpen(true)}
            >
              <ParticipantAvatars trip={trip} />
            </SettingRow>
            {/*
              "Share invite link" is open to any accepted member (owner check
              done at the Firestore-rule layer, not here) — matches the
              product decision to let travelers loop friends in. Owner still
              controls revocation.
            */}
            <SettingRow
              label="Share invite link"
              icon={Link2}
              onPress={() => setInviteLinksOpen(true)}
            />
          </Section>

          <Section title="Details">
            <SettingRow
              label="Dates"
              value={`${trip.durationDays} night${trip.durationDays === 1 ? "" : "s"}`}
              icon={Calendar}
              onPress={isOwner ? () => setDatesOpen(true) : undefined}
            />
            <SettingRow
              label="Currency"
              value={trip.currency ?? "EUR"}
              icon={DollarSign}
              onPress={isOwner ? () => setCurrencyOpen(true) : undefined}
            />
            <SettingRow
              label="Budget"
              value={
                trip.budget
                  ? formatMoney(trip.budget.amount, trip.budget.currency)
                  : "None"
              }
              icon={DollarSign}
              onPress={isOwner ? () => setBudgetOpen(true) : undefined}
            />
            <SettingRow
              label="Notifications"
              icon={Bell}
              onPress={() => toast.info("Notification settings — post-MVP")}
            />
          </Section>

          <Section title="Memory">
            <RecapRow tripId={trip.id} />
          </Section>

          <Section title={isOwner ? "Danger zone" : "Leave"}>
            {!isOwner ? (
              <SettingRow
                label="Leave trip"
                icon={LogOut}
                tone="warning"
                onPress={() => setLeaveOpen(true)}
              />
            ) : null}
            {isOwner ? (
              <SettingRow
                label="Delete trip"
                icon={Trash2}
                tone="danger"
                onPress={() => setDeleteOpen(true)}
              />
            ) : null}
          </Section>
        </div>
      )}

      {trip ? (
        <>
          <RenameDialog open={renameOpen} onOpenChange={setRenameOpen} trip={trip} />
          <EditDatesDialog open={datesOpen} onOpenChange={setDatesOpen} trip={trip} />
          <CurrencyDialog open={currencyOpen} onOpenChange={setCurrencyOpen} trip={trip} />
          <BudgetDialog open={budgetOpen} onOpenChange={setBudgetOpen} trip={trip} />
          <ParticipantsSheet
            open={participantsOpen}
            onOpenChange={setParticipantsOpen}
            trip={trip}
            isOwner={isOwner}
            currentUid={user?.uid ?? null}
            currentEmail={currentEmail}
          />
          <InviteLinksSheet
            open={inviteLinksOpen}
            onOpenChange={setInviteLinksOpen}
            trip={trip}
          />
          {!isOwner && user ? (
            <LeaveDialog open={leaveOpen} onOpenChange={setLeaveOpen} trip={trip} />
          ) : null}
          {isOwner ? (
            <DeleteDialog open={deleteOpen} onOpenChange={setDeleteOpen} trip={trip} />
          ) : null}
        </>
      ) : null}
    </main>
  );
}

// ─── Section shell ───────────────────────────────────────────
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <p className="mb-2 px-1 text-[11px] font-bold uppercase tracking-widest text-ink-subtle">
        {title}
      </p>
      <div className="overflow-hidden rounded-xl border border-line bg-bg-elevated divide-y divide-line">
        {children}
      </div>
    </section>
  );
}

function SettingRow({
  label,
  value,
  icon: Icon,
  tone = "default",
  onPress,
  children,
}: {
  label: string;
  value?: string;
  icon?: React.ComponentType<{ className?: string }>;
  tone?: "default" | "warning" | "danger";
  onPress?: () => void;
  children?: React.ReactNode;
}) {
  const toneCls =
    tone === "danger" ? "text-danger" : tone === "warning" ? "text-warning" : "text-ink";
  return (
    <button
      type="button"
      onClick={onPress}
      disabled={!onPress}
      className="flex w-full items-center gap-3 px-4 py-3.5 text-left disabled:cursor-default hover:bg-bg-sunken/40 disabled:opacity-70"
    >
      {Icon ? <Icon className={`h-4 w-4 ${toneCls}`} /> : null}
      <span className={`flex-1 text-[15px] font-medium ${toneCls}`}>{label}</span>
      {value ? <span className="text-[13px] text-ink-muted">{value}</span> : null}
      {children}
      {onPress ? <ChevronRight className="h-4 w-4 text-ink-subtle" /> : null}
    </button>
  );
}

function RecapRow({ tripId }: { tripId: string }) {
  return (
    <Link
      to={`/trip/${tripId}/recap`}
      className="flex w-full items-center gap-3 px-4 py-3.5 hover:bg-bg-sunken/40"
    >
      <Sparkles className="h-4 w-4 text-primary" />
      <span className="flex-1 text-[15px] font-medium text-ink">See recap</span>
      <ChevronRight className="h-4 w-4 text-ink-subtle" />
    </Link>
  );
}

function ParticipantAvatars({ trip }: { trip: Trip }) {
  const entries = Object.entries(trip.members);
  const shown = entries.slice(0, 3);
  const remaining = entries.length - shown.length;
  return (
    <div className="flex items-center gap-2">
      <AvatarGroup>
        {shown.map(([uid, m], i) => (
          <Avatar key={uid} name={m.name} colorSeed={i} size="sm" />
        ))}
      </AvatarGroup>
      {remaining > 0 ? (
        <span className="text-[13px] text-ink-muted">+{remaining}</span>
      ) : null}
      <Users className="ml-2 h-4 w-4 text-ink-subtle" />
    </div>
  );
}

// ─── Cover photo (display-only for now) ──────────────────────
// v2 has no file uploads on purpose — see docs/decisions.md D-015. Trips
// display either their existing coverImage (if v1 set one) or the warm
// gradient stand-in from TripCard/hero. The "Change photo" affordance
// stays visible so owners see it's planned.
function CoverPhotoRow({ trip, isOwner }: { trip: Trip; isOwner: boolean }) {
  const [imgBroken, setImgBroken] = useState(false);
  const showImage =
    !!trip.coverImage && !imgBroken && !isLikelyBrokenCoverUrl(trip.coverImage);
  return (
    <div className="mx-auto flex flex-col items-center gap-2">
      <div className="relative block h-32 w-56 overflow-hidden rounded-xl border border-line shadow-warm-2">
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(135deg,#E4572E,#7A2712)" }}
          aria-hidden
        />
        {showImage ? (
          <img
            src={trip.coverImage!}
            alt=""
            onError={() => setImgBroken(true)}
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : null}
        {isOwner ? (
          <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1.5 bg-black/45 py-1.5 backdrop-blur-sm">
            <Camera className="h-3 w-3 text-white/90" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-white/90">
              Change photo · Soon
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ─── Rename trip (owner) ─────────────────────────────────────
function RenameDialog({
  open,
  onOpenChange,
  trip,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  trip: Trip;
}) {
  const [name, setName] = useState(trip.name);
  const [err, setErr] = useState<string | null>(null);

  const mutation = useTripMutation({
    keys: [queryKeys.trip(trip.id), queryKeys.users()],
    mutationFn: async () => {
      const next = name.trim();
      if (!next) throw new Error("Give it a name.");
      if (next === trip.name) return;
      await unwrap(updateTripName(trip.id, next));
    },
    successToast: "Trip renamed.",
    onError: (e) => setErr(e instanceof Error ? e.message : "Couldn't rename."),
    onSuccess: () => onOpenChange(false),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader title="Rename trip" />
        <form
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
        >
          <Field id="rn-name" label="Trip name" error={err ?? undefined}>
            <Input
              id="rn-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (err) setErr(null);
              }}
              autoFocus
              maxLength={80}
            />
          </Field>
          <DialogFooter>
            <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending || !name.trim()}>
              {mutation.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Edit dates (owner) ──────────────────────────────────────
function EditDatesDialog({
  open,
  onOpenChange,
  trip,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  trip: Trip;
}) {
  const [start, setStart] = useState(() => toDateInput(trip.startDate));
  const [end, setEnd] = useState(() => toDateInput(trip.endDate));
  const [err, setErr] = useState<string | null>(null);

  const mutation = useTripMutation({
    keys: [queryKeys.trip(trip.id), queryKeys.users()],
    mutationFn: async () => {
      // Parse the yyyy-MM-dd values from `<input type="date">` as LOCAL
      // midnight — `new Date("yyyy-MM-dd")` is UTC and shifts the day
      // west of UTC (see parseYmdLocal for the timezone story).
      const startLocal = parseYmdLocal(start);
      const endLocal = parseYmdLocal(end);
      if (!startLocal || !endLocal) throw new Error("Pick valid dates.");
      if (endLocal.getTime() < startLocal.getTime())
        throw new Error("End date can't be before start.");
      await unwrap(updateTripSummaryFields(trip.id, {
        startDate: Timestamp.fromDate(startLocal),
        endDate: Timestamp.fromDate(endLocal),
      }));
    },
    successToast: "Dates updated.",
    onError: (e) => setErr(e instanceof Error ? e.message : "Couldn't update dates."),
    onSuccess: () => onOpenChange(false),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          title="Edit dates"
          description="Existing activities keep their original times — you may want to move them if they now fall outside the trip."
        />
        <form
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
          className="flex flex-col gap-3"
        >
          <div className="grid grid-cols-2 gap-3">
            <Field id="d-start" label="Starts">
              <Input id="d-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
            </Field>
            <Field id="d-end" label="Ends">
              <Input id="d-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
            </Field>
          </div>
          {err ? <p role="alert" className="text-[12px] text-danger">{err}</p> : null}
          <DialogFooter>
            <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function toDateInput(ts: Timestamp): string {
  const d = ts.toDate();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// ─── Currency (owner) ────────────────────────────────────────
function CurrencyDialog({
  open,
  onOpenChange,
  trip,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  trip: Trip;
}) {
  const [choice, setChoice] = useState<string>(trip.currency ?? "EUR");

  const mutation = useTripMutation({
    keys: [queryKeys.trip(trip.id)],
    mutationFn: () => unwrap(updateTripCurrency(trip.id, choice)),
    successToast: `Currency set to ${choice}.`,
    errorToast: "Couldn't update currency.",
    onSuccess: () => onOpenChange(false),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          title="Trip currency"
          description="This affects how amounts are displayed. Existing expenses keep the currency they were logged in."
        />
        <div className="grid grid-cols-4 gap-2">
          {SUPPORTED_CURRENCIES.map((c) => {
            const on = choice === c;
            return (
              <button
                key={c}
                type="button"
                onClick={() => setChoice(c)}
                className={cn(
                  "rounded-md border p-3 text-[13px] font-semibold transition-colors",
                  on
                    ? "border-primary bg-primary-soft text-primary-ink"
                    : "border-line text-ink hover:bg-bg-sunken",
                )}
              >
                {c}
                {on ? <Check className="mx-auto mt-1 h-3 w-3" /> : null}
              </button>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Budget dialog ──────────────────────────────────────────
function BudgetDialog({
  open,
  onOpenChange,
  trip,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  trip: Trip;
}) {
  const [amount, setAmount] = useState<string>(
    trip.budget ? String(trip.budget.amount) : "",
  );
  const [ccy, setCcy] = useState<string>(
    trip.budget?.currency ?? trip.currency ?? "EUR",
  );

  const mutation = useTripMutation({
    keys: [queryKeys.trip(trip.id)],
    mutationFn: (next: TripBudget | null) => unwrap(updateTripBudget(trip.id, next)),
    successToast: "Budget updated.",
    errorToast: "Couldn't update budget.",
    onSuccess: () => onOpenChange(false),
  });

  const num = parseFloat(amount.replace(",", "."));
  const valid = Number.isFinite(num) && num > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          title="Trip budget"
          description="An optional target for the group. Shown as a progress bar on the expenses tab. Clear it any time."
        />
        <div className="flex flex-col gap-4">
          <Field id="budget-amount" label="Amount">
            <div className="flex items-center gap-2">
              <Input
                id="budget-amount"
                type="text"
                inputMode="decimal"
                placeholder="0.00"
                value={amount}
                onChange={(e) =>
                  setAmount(e.target.value.replace(/[^\d.,]/g, ""))
                }
              />
              <label className="inline-flex items-center rounded-full bg-bg-sunken px-2.5 py-1.5 text-[12px] font-semibold text-ink">
                <select
                  aria-label="Budget currency"
                  value={ccy}
                  onChange={(e) => setCcy(e.target.value)}
                  className="bg-transparent text-ink outline-none"
                >
                  {SUPPORTED_CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </Field>
        </div>
        <DialogFooter>
          {trip.budget ? (
            <Button
              variant="ghost"
              onClick={() => mutation.mutate(null)}
              disabled={mutation.isPending}
            >
              Clear
            </Button>
          ) : null}
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate({ amount: num, currency: ccy })}
            disabled={mutation.isPending || !valid}
          >
            {mutation.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Participants sheet ─────────────────────────────────────
function ParticipantsSheet({
  open,
  onOpenChange,
  trip,
  isOwner,
  currentUid,
  currentEmail,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  trip: Trip;
  isOwner: boolean;
  currentUid: string | null;
  currentEmail: string | null;
}) {

  const remove = useTripMutation({
    keys: [queryKeys.trip(trip.id), queryKeys.users()],
    mutationFn: (args: { uid: string; email: string }) =>
      unwrap(removeMemberFromTrip(trip.id, args.uid, args.email)),
    successToast: "Member removed.",
    errorToast: "Couldn't remove that member.",
  });

  // Removing a member is confirmed first — it drops them from the
  // trip for everyone and a stray tap on "Remove" shouldn't do that.
  const [pendingRemove, setPendingRemove] = useState<{ uid: string; email: string; name: string } | null>(null);

  const members = Object.entries(trip.members) as [string, TripMemberEntry][];
  const pending = trip.pendingInviteEmails ?? [];

  return (
    <>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader
          title="Members"
          description={isOwner ? "Invite people by email, or remove members." : "Invite people by email. Only the owner can remove members."}
        />
        <SheetBody className="flex flex-col gap-4">
          <div>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-ink-subtle">
              In the trip · {members.length}
            </p>
            <ul className="flex flex-col divide-y divide-line rounded-xl border border-line bg-bg-elevated">
              {members.map(([uid, m], i) => {
                // Match by UID first (canonical), then fall back to email
                // to cover v1 trips whose members{} is keyed by email.
                const you =
                  uid === currentUid ||
                  (!!currentEmail && m.email?.toLowerCase() === currentEmail);
                const canRemove = isOwner && !you && m.role !== "owner";
                return (
                  <li key={uid} className="flex items-center gap-3 px-4 py-3">
                    <Avatar name={m.name} colorSeed={i} size="md" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[15px] font-semibold text-ink m-0 truncate">
                        {m.name}
                        {you ? <span className="ml-1.5 text-[12px] font-normal text-ink-subtle">· you</span> : null}
                      </p>
                      <p className="text-[12px] text-ink-subtle m-0">
                        {m.role[0]!.toUpperCase() + m.role.slice(1)} · {m.email}
                      </p>
                    </div>
                    {canRemove ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPendingRemove({ uid, email: m.email, name: m.name })}
                        disabled={remove.isPending}
                        className="text-danger hover:bg-danger-soft/40"
                      >
                        Remove
                      </Button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>

          {pending.length > 0 ? (
            <div>
              <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-ink-subtle">
                Invited · {pending.length}
              </p>
              <ul className="flex flex-col divide-y divide-line rounded-xl border border-line bg-bg-sunken/50">
                {pending.map((email) => (
                  <li key={email} className="px-4 py-3 text-[13px] text-ink-muted">
                    {email} <span className="text-ink-subtle">· waiting</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <InviteInput trip={trip} />
        </SheetBody>
        <SheetFooter>
          <Button variant="ghost" block onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>

    <ConfirmDialog
      open={!!pendingRemove}
      onOpenChange={(v) => !v && setPendingRemove(null)}
      title={`Remove ${pendingRemove?.name ?? "this member"}?`}
      description="They lose access to the trip for everyone. You can invite them again later."
      confirmLabel="Remove"
      destructive
      onConfirm={async () => {
        if (pendingRemove) await remove.mutateAsync({ uid: pendingRemove.uid, email: pendingRemove.email });
        setPendingRemove(null);
      }}
    />
    </>
  );
}

// ─── Invite input (any member, inside Members sheet) ─────────
function InviteInput({ trip }: { trip: Trip }) {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const emailRegex = /^[^\s@/]+@[^\s@/]+\.[^\s@/]+$/;

  const alreadyMember = (target: string) => {
    const t = target.toLowerCase();
    if (Object.values(trip.members).some((m) => m.email?.toLowerCase() === t)) return true;
    if ((trip.pendingInviteEmails ?? []).some((e) => e.toLowerCase() === t)) return true;
    return false;
  };

  const invite = useTripMutation({
    keys: [queryKeys.trip(trip.id)],
    mutationFn: async () => {
      const target = email.trim().toLowerCase();
      if (!target) throw new Error("Enter an email.");
      if (!emailRegex.test(target)) throw new Error("That doesn't look like an email.");
      if (alreadyMember(target)) throw new Error("They're already in the trip.");
      if (!user?.uid) throw new Error("Not signed in.");

      // The server re-checks membership against fresh data; a stale cache
      // here just means a friendlier toast.
      const status = await unwrap(inviteUserToTrip(trip.id, target, "traveler"));
      if (status === "already-member") throw new Error("They're already in the trip.");
      return target;
    },
    onSuccess: (target) => {
      toast.success(`Invited ${target}`, {
        description: "They'll see the invite when they open Travelix.",
      });
      setEmail("");
      setErr(null);
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : "Couldn't send the invite.";
      setErr(msg);
    },
  });

  return (
    <div>
      <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-ink-subtle">
        Invite by email
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          invite.mutate();
        }}
        className="flex gap-2"
      >
        <Input
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="friend@example.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (err) setErr(null);
          }}
          invalid={!!err}
          className="flex-1"
        />
        <Button type="submit" disabled={invite.isPending || !email.trim()}>
          {invite.isPending ? "Sending…" : "Invite"}
        </Button>
      </form>
      {err ? (
        <p role="alert" className="mt-1.5 text-[12px] text-danger">
          {err}
        </p>
      ) : (
        <p className="mt-1.5 text-[12px] text-ink-subtle">
          They'll see the trip when they open Travelix. Emails come later.
        </p>
      )}
    </div>
  );
}

// ─── Leave trip (non-owner) ──────────────────────────────────
function LeaveDialog({
  open,
  onOpenChange,
  trip,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  trip: Trip;
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const leave = useTripMutation({
    keys: [queryKeys.users()],
    mutationFn: async () => {
      if (!user?.uid || !user.email) throw new Error("Not signed in.");
      await unwrap(leaveTrip(trip, user.uid, user.email));
    },
    successToast: "You've left the trip.",
    errorToast: "Couldn't leave the trip.",
    onSuccess: () => {
      onOpenChange(false);
      navigate("/dashboard", { replace: true });
    },
  });

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Leave this trip?"
      description="You won't see it in your list anymore. Existing expense splits you're part of stay in the trip's records."
      confirmLabel="Leave trip"
      destructive
      // Errors are toasted by the mutation; the dialog stays open.
      onConfirm={() => leave.mutateAsync().catch(() => undefined)}
    />
  );
}

// ─── Delete trip (owner) ─────────────────────────────────────
function DeleteDialog({
  open,
  onOpenChange,
  trip,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  trip: Trip;
}) {
  const navigate = useNavigate();
  const remove = useTripMutation({
    keys: [queryKeys.users()],
    mutationFn: () =>
      unwrap(deleteTrip(trip.id, Object.values(trip.members).map((m) => m.email))),
    successToast: "Trip deleted.",
    errorToast: "Couldn't delete the trip.",
    onSuccess: () => {
      onOpenChange(false);
      navigate("/dashboard", { replace: true });
    },
  });

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Delete this trip?"
      description="This can't be undone. Every activity, expense, and split disappears for everyone in the trip."
      confirmLabel="Delete forever"
      destructive
      typeToConfirm={trip.name}
      // Errors are toasted by the mutation; the dialog stays open.
      onConfirm={() => remove.mutateAsync().catch(() => undefined)}
    />
  );
}
